import { useForm } from 'react-hook-form'

import { TextSpinner } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { zodResolver } from '@hookform/resolvers/zod'

import i18n from 'modules/core/lib/i18n'

import { FormContent } from 'modules/shift_management/components/ShiftManagement/ImportEmployeesModal/FormContent'
import { useSelectableEmployeesByCurrent } from 'modules/shift_management/hooks/useSelectableEmployeesByCurrent'
import { Form } from 'modules/shift_management/lib/forms/ImportEmployeesToSM/form'
import { ImportEmployeesToSMSchemaType } from 'modules/shift_management/lib/forms/ImportEmployeesToSM/schema'

import useImportEmployeesSM from './useImportEmployeesSM'

interface Props {
  onClose: () => void
}

// I need to import employees that I have permissions to create shifts
// but they might not be using shift management yet
const ImportEmployeesModal = ({ onClose }: Props) => {
  const { defaultValues, schema } = Form()
  const form = useForm<ImportEmployeesToSMSchemaType>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { watch } = form
  const employeeSelection = watch('employeeSelection')
  const effectiveAt = watch('effectiveAt')
  const numberOfRestDays = watch('numberOfRestDaysInCents')
  const { employeeIds: selectableEmployeeIds } = useSelectableEmployeesByCurrent()
  const selectableEmployeesCount = selectableEmployeeIds.length
  const { onSubmit, submitting, dateError, clearDateError } = useImportEmployeesSM({
    effectiveAt,
    employeeSelection,
    numberOfRestDays,
    selectableEmployeesCount,
    onSuccess: () => onClose(),
  })

  const disabled =
    (employeeSelection.type === 'manual' && employeeSelection.ids?.length === 0) ||
    !effectiveAt ||
    submitting ||
    !!dateError

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={onClose}
      title={i18n.t('shift_management.import_employees.title')}
      description={i18n.t('shift_management.import_employees.description')}
      primaryAction={{
        label: i18n.t('shift_management.import_employees.action'),
        onClick: onSubmit,
        disabled,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {submitting && <TextSpinner text={i18n.t('shift_management.actions.adding_employees')} />}
      {!submitting && (
        <FormContent
          form={form}
          selectableEmployeeIds={selectableEmployeeIds}
          dateError={dateError}
          onDateChange={clearDateError}
        />
      )}
    </F0Dialog>
  )
}

export default ImportEmployeesModal
